import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  WavelightWorkflowState,
  WorkflowProcessData,
  WorkflowStartResponse,
  WorkflowVideoUploadResponse,
} from './types';
import {
  Gender,
  RacialDenomination,
  WavelightMode,
  WorkflowMeasurements,
  WorkflowPerson,
} from '../../types';

import {
  confirmVideoUpload,
  finishWorkflow,
  getPreSignedURL,
  startWorkflow,
  updateMeasurements,
  updateMetaData,
  updatePersonData,
  updateStudyData,
  uploadFileOn,
} from './userAPI';

import storage from '../../utils/storage';

// Update Geo Location for Meta Data
export const setMetaData = createAsyncThunk(
  'wavelight/gelo-location',
  async (_, { rejectWithValue }) => {
    try {
      const position: any = await new Promise((resolve, reject) => {
        if ('geolocation' in navigator) {
          navigator.geolocation.getCurrentPosition(resolve, reject);
        } else {
          reject('Geolocation is not supported by this browser.');
        }
      });

      // Após obter sucesso na localização, retorna os dados necessários.
      return {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };
    } catch (error) {
      // Caso haja algum erro, rejeita a Promise com o valor do erro.
      return rejectWithValue(error);
    }
  },
);

// Start and Finish the entire Workflow process...
export const uploadVideoAndData = createAsyncThunk(
  'wavelight/workflow',
  async (data: WorkflowProcessData) => {
    // 1. Start the workflow by creating a ticket
    const ticket: WorkflowStartResponse = await startWorkflow(
      WavelightMode.ANALYSIS,
    );
    const wId = ticket.id;
    // 2. Get Presigned Url for video upload
    const extension = `.${data.video.name.split('.')[1]}`;
    const fileUploadRequest: WorkflowVideoUploadResponse =
      await getPreSignedURL(wId, { extension });
    // 3. Upload File
    await uploadFileOn(fileUploadRequest, data.video);
    // 4. Confirm uploaded file
    await confirmVideoUpload(wId);
    // 5. Send Person Data
    await updatePersonData(wId, data.person);
    // 6. Send Study Data
    await updateStudyData(wId, data.study);
    // 7. Send Measurements
    await updateMeasurements(wId, data.measurements);
    // 8. Send Metadatas
    await updateMetaData(wId, data.metadata);
    // 9. Close this Workflow
    await finishWorkflow(wId);

    return {
      id: wId,
    };
  },
);

const OXIMETER_TAGS = '@wavelight/OXIMETER_TAGS';
const PRESSURE_GAUGE = '@wavelight/PRESSURE_GAUGE';
const CELLPHONE = '@wavelight/CELLPHONE';
const GLUCOSE_EXTRA_INFO = '@wavelight/GLUCOSE_EXTRA_INFO';
const TAGS = '@wavelight/TAGS';

const initialState: WavelightWorkflowState = {
  // Current Wavelight ID
  wavelightId: null,
  // Loading
  isLoading: false,
  // Upload Completed
  uploadCompleted: false,
  // Data
  data: {
    video: null,
    study: {
      tags: storage.get(TAGS) || [],
      phone: storage.get(CELLPHONE) || [],
      oximeterModel: storage.get(OXIMETER_TAGS) || [],
      pressureGaugeModel: storage.get(PRESSURE_GAUGE) || [],
      glucoseExtraInfo: storage.get(GLUCOSE_EXTRA_INFO) || [],
      extraDescription: '',
    },
    person: {
      alias: '',
      height: 0,
      weight: 0,
      age: 0,
      gender: Gender.UNDEFINED,
      racialDenomination: RacialDenomination.UNDEFINED,
      extraDescription: '',
    },
    measurements: {
      bpm: 0,
      bloodPreassure: {
        systolic: 0,
        diastolic: 0,
      },
      bloodO2Saturation: 0,
      respiratoryRate: 0,
      glucose: 0,
      ignore: {
        ignoreAll: false,
        ignoreBpm: false,
        ignorebloodO2Saturation: false,
        ignorebloodPreassure: false,
        ignoreRespiratoryRate: false,
        ignoreGlucose: false,
      },
    },
    metadata: {
      browserInfo: {
        userAgent: navigator.userAgent,
        language: navigator.language,
      },
      screenInfo: {
        width: window.screen.width,
        height: window.screen.height,
      },
      geoLocalization: null,
    },
  },
};

export const captureSlice = createSlice({
  name: 'workflow',
  initialState,
  reducers: {
    // Controll
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    resetState: () => initialState,
    // Video
    setVideo: (state, action: PayloadAction<File>) => {
      state.data.video = action.payload;
    },
    // Study
    setTags: (state, action: PayloadAction<string[]>) => {
      state.data.study.tags = action.payload;
      storage.set(TAGS, action.payload);
    },
    updatePhoneInfo: (state, action: PayloadAction<string[]>) => {
      state.data.study.phone = action.payload;
      storage.set(CELLPHONE, action.payload);
    },
    updateOximeterModel: (state, action: PayloadAction<string[]>) => {
      state.data.study.oximeterModel = action.payload;
      storage.set(OXIMETER_TAGS, action.payload);
    },
    updatePreassureGauge: (state, action: PayloadAction<string[]>) => {
      state.data.study.pressureGaugeModel = action.payload;
      storage.set(PRESSURE_GAUGE, action.payload);
    },
    updateGlucoseExtraInfo: (state, action: PayloadAction<string[]>) => {
      state.data.study.glucoseExtraInfo = action.payload;
      storage.set(GLUCOSE_EXTRA_INFO, action.payload);
    },
    setExtraDescriptionForStudy: (state, action: PayloadAction<string>) => {
      state.data.study.extraDescription = action.payload;
    },
    // Personal data
    setPerson: (state, action: PayloadAction<WorkflowPerson>) => {
      state.data.person = action.payload;
    },
    // Measurements
    setMeasurements: (state, action: PayloadAction<WorkflowMeasurements>) => {
      state.data.measurements = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Upload Video
    builder.addCase(uploadVideoAndData.fulfilled, (state, { payload }) => {
      state.isLoading = false;
      state.uploadCompleted = true;
      state.wavelightId = payload.id;
    });
    // Meta Data - Geo Localization
    builder.addCase(setMetaData.fulfilled, (state, { payload }) => {
      state.data.metadata.geoLocalization = {
        latitude: payload.latitude,
        longitude: payload.longitude,
      };
    });
    builder.addCase(setMetaData.rejected, (state, { payload }) => {
      state.data.metadata.geoLocalization = null;
    });
  },
});

export const {
  // Controll
  setLoading,
  resetState,
  // Video
  setVideo,
  // Study
  setTags,
  updatePhoneInfo,
  updateOximeterModel,
  updatePreassureGauge,
  updateGlucoseExtraInfo,
  setExtraDescriptionForStudy,
  // Personal data
  setPerson,
  // Measurements
  setMeasurements,
} = captureSlice.actions;

export default captureSlice.reducer;
